import { useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
} from "@wondermaps/ui";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons/faMagnifyingGlass";
import { faLocationArrow } from "@fortawesome/pro-solid-svg-icons/faLocationArrow";
import { faDownload } from "@fortawesome/pro-solid-svg-icons/faDownload";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons/faSpinner";
import type { SponsorLogo, SponsorTierGroup } from "./sponsor-types";
import { SIZE_BUCKET_CLASSES } from "./sponsor-size-buckets";

interface Branding {
  logoUrl?: string | null;
  brandColor?: string | null;
}

interface WelcomeModalProps {
  mapName: string;
  branding: Branding | null;
  isMapReady: boolean;
  onDismiss: () => void;
  sponsors?: SponsorTierGroup[];
  onSponsorClick?: (sponsor: SponsorLogo, placement: string) => void;
  onSponsorImpression?: (sponsors: SponsorLogo[], placement: string) => void;
}

const FEATURE_ICONS = [faMagnifyingGlass, faLocationArrow, faDownload] as const;
const FEATURE_KEYS = ["search", "directions", "offline"] as const;

/**
 * First-visit welcome modal that onboards visitors to key features
 * while background assets finish loading.
 */
export function WelcomeModal({
  mapName,
  branding,
  isMapReady,
  onDismiss,
  sponsors,
  onSponsorClick,
  onSponsorImpression,
}: WelcomeModalProps) {
  const { t } = useTranslation("common");
  const logoUrl = branding?.logoUrl ?? null;
  const brandColor = branding?.brandColor || "#1f2937";

  const hasSponsors = sponsors && sponsors.some((tier) => tier.sponsors.length > 0);

  return (
    <Dialog open onOpenChange={(open) => !open && onDismiss()}>
      <DialogContent className="max-w-md rounded-xl p-6 sm:max-w-md">
        <DialogHeader className="text-center sm:text-center">
          {/* Logo */}
          {logoUrl && (
            <div className="mx-auto mb-2">
              <img
                src={logoUrl}
                alt={mapName}
                className="max-h-16 max-w-32 object-contain"
              />
            </div>
          )}
          <DialogTitle className="text-xl font-semibold">
            {t("welcome.title", { mapName })}
          </DialogTitle>
        </DialogHeader>

        {/* Feature highlights */}
        <div className="mt-4 space-y-4">
          {FEATURE_KEYS.map((key, i) => {
            const icon = FEATURE_ICONS[i];
            return (
              <div key={key} className="flex items-start gap-3">
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                  style={{ backgroundColor: `${brandColor}15` }}
                >
                  <FontAwesomeIcon
                    icon={icon}
                    className="h-5 w-5"
                    style={{ color: brandColor }}
                  />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">{t(`welcome.${key}Title`)}</p>
                  <p className="text-sm text-gray-500">{t(`welcome.${key}Description`)}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Button */}
        <div className="mt-6">
          <Button
            onClick={onDismiss}
            className="w-full"
            style={{ backgroundColor: brandColor }}
          >
            {t("welcome.continueToMap")}
          </Button>
        </div>

        {/* Sponsor logos (clickable) */}
        {hasSponsors && (
          <WelcomeSponsors
            sponsors={sponsors!}
            onSponsorClick={onSponsorClick}
            onImpression={onSponsorImpression}
          />
        )}

        {/* Loading indicator */}
        {!isMapReady && (
          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-400">
            <FontAwesomeIcon icon={faSpinner} spin className="h-4 w-4 " />
            <span>{t("splash.preparingMap")}</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function WelcomeSponsorRow({
  sponsors,
  className,
  onSponsorClick,
}: {
  sponsors: SponsorLogo[];
  className: string;
  onSponsorClick?: (sponsor: SponsorLogo, placement: string) => void;
}) {
  if (sponsors.length === 0) return null;
  return (
    <div className="flex items-center justify-center gap-3">
      {sponsors.map((s) =>
        s.logoUrl ? (
          <a
            key={s.id}
            href={s.websiteUrl || undefined}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              if (!s.websiteUrl) e.preventDefault();
              onSponsorClick?.(s, "welcome_modal");
            }}
            className="transition-opacity hover:opacity-80"
          >
            <img
              src={s.logoUrl}
              alt={s.name || "Sponsor"}
              className={`${className} object-contain`}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </a>
        ) : null
      )}
    </div>
  );
}

function WelcomeSponsors({
  sponsors,
  onSponsorClick,
  onImpression,
}: {
  sponsors: SponsorTierGroup[];
  onSponsorClick?: (sponsor: SponsorLogo, placement: string) => void;
  onImpression?: (sponsors: SponsorLogo[], placement: string) => void;
}) {
  const { t } = useTranslation("common");
  const allSponsors = useMemo(() => sponsors.flatMap((tier) => tier.sponsors), [sponsors]);

  // eslint-disable-next-line react-hooks/exhaustive-deps — fire-once impression on mount
  useEffect(() => {
    onImpression?.(allSponsors, "welcome_modal");
  }, []);

  return (
    <div className="mt-4 flex flex-col items-center gap-2">
      <p className="text-xs text-gray-400">{t("sponsors.presentedBy")}</p>
      {sponsors.map((tier) => (
        <WelcomeSponsorRow
          key={tier.slug}
          sponsors={tier.sponsors}
          className={SIZE_BUCKET_CLASSES.welcome[tier.sizeBucket] ?? SIZE_BUCKET_CLASSES.welcome.small}
          onSponsorClick={onSponsorClick}
        />
      ))}
    </div>
  );
}
